import React, { useMemo, useState, useCallback } from 'react'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import GradingState from '@/components/organisms/Grading/GradingState'
import { Button } from '@/components/atoms'
import { Dialog } from '@/components/organisms/Modal/Dialog'
import RecordingGradingContents from '@/components/Modal/RecordingGradingContents'
import { academyLearningScheduleApi } from 'services/api'


interface SentenceItem {
  id: string
  text: string
  audioUrl: string | null
  role: 'Q' | 'A'
}

interface GradingStoryDialogProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
  manualGradingResults?: Record<number, 'correct' | 'incorrect' | 'grading'>
  onManualGradingChange?: (sentenceIdx: number, status: 'correct' | 'incorrect' | undefined) => void
  canUseAi?: boolean
  lockedManualKeys?: Set<string>
}

const deriveRole = (content: any, index: number): 'Q' | 'A' => {
  const rawRole = String(
    content?.role ||
      content?.speaker ||
      content?.type ||
      content?.tag ||
      (typeof content?.isQuestion === 'boolean' ? (content.isQuestion ? 'Q' : 'A') : '')
  ).toUpperCase();

  if (rawRole.includes('Q') || rawRole.includes('QUESTION') || rawRole.includes('TEACHER')) {
    return 'Q';
  }
  if (rawRole.includes('A') || rawRole.includes('ANSWER') || rawRole.includes('STUDENT')) {
    return 'A';
  }
  return index % 2 === 0 ? 'Q' : 'A';
};

const GradingStoryDialog = ({ type = 'scoreCard', activity, responseJson, manualGradingResults, onManualGradingChange, canUseAi = true, lockedManualKeys }: GradingStoryDialogProps) => {
  // 1) 문장 데이터 파싱 (activity.items[*].contentJson.sentence)
  const sentenceItems: SentenceItem[] = useMemo(() => {
    try {
      const items = activity?.items || []
      return items.map((it: any, index: number) => {
        const c = it?.contentJson ? (typeof it.contentJson === 'string' ? JSON.parse(it.contentJson) : it.contentJson) : {}
        return {
          id: String(it?.id ?? `sentence-${index}`),
          text: c.sentence || c.word || c.text || '',
          audioUrl: typeof c.audioUrl === 'string' ? c.audioUrl : (typeof c.audio === 'string' ? c.audio : null),
          role: deriveRole(c, index)
        }
      }).filter((item: SentenceItem) => item.text || item.audioUrl)
    } catch (e) {
      console.error('StoryDialog: sentences 파싱 실패', e)
      return []
    }
  }, [activity])

  // 2) 응답 파싱: sentenceRecordings 또는 인덱스별 recordingUrl 지원
  const recordings: (string | null)[] = useMemo(() => {
    let resp: any = {}
    try { if (responseJson) resp = JSON.parse(responseJson) } catch {}
    const out: (string | null)[] = []
    if (resp && resp.sentenceRecordings) {
      sentenceItems.forEach((_, idx) => {
        const rec = resp.sentenceRecordings?.[`sentence-${idx}`]
        out.push(typeof rec?.url === 'string' ? rec.url : null)
      })
      return out
    }
    // 대안: resp가 배열/객체로 index key를 가진 경우
    sentenceItems.forEach((_, idx) => {
      const entry = resp?.[idx] || resp?.[String(idx)] || {}
      out.push(typeof entry?.recordingUrl === 'string' ? entry.recordingUrl : null)
    })
    return out
  }, [responseJson, sentenceItems])

  // 3) 상태 관리: 문장별 채점 상태 + 다이얼로그
  const [perSentenceStatus, setPerSentenceStatus] = useState<Array<'correct' | 'incorrect' | 'grading'>>(() => {
    if (manualGradingResults && Object.keys(manualGradingResults).length > 0) {
      return sentenceItems.map((_, idx) => manualGradingResults[idx] || 'grading');
    }
    return sentenceItems.map(() => 'grading');
  })
  
  // 저장된 결과가 변경되면 상태 업데이트
  React.useEffect(() => {
    if (manualGradingResults && Object.keys(manualGradingResults).length > 0) {
      const newStatuses = sentenceItems.map((_, idx) => manualGradingResults[idx] || 'grading');
      setPerSentenceStatus(newStatuses);
    }
  }, [manualGradingResults, sentenceItems]);
  
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogIndex, setDialogIndex] = useState<number | null>(null)
  const [dialogStatus, setDialogStatus] = useState<'correct' | 'incorrect'>('correct')
  const [dialogLocked, setDialogLocked] = useState(false)
  const [aiLoadingSet, setAiLoadingSet] = useState<Set<number>>(new Set())
  const [aiUsedSet, setAiUsedSet] = useState<Set<number>>(new Set())
  const activityId = activity?.id != null ? String(activity.id) : null;
  const isSentenceLocked = useCallback((idx: number) => {
    if (!activityId) return false;
    const variants = [`${activityId}-sentence-${idx}`, `sd-${activityId}-${idx}`];
    return variants.some((key) => lockedManualKeys?.has(key));
  }, [activityId, lockedManualKeys]);

  React.useEffect(() => {
    setAiUsedSet(new Set());
  }, [activity]);

  const openDialog = (index: number) => {
    setDialogIndex(index)
    const current = perSentenceStatus[index]
    setDialogStatus(current === 'incorrect' ? 'incorrect' : 'correct')
    setDialogLocked(isSentenceLocked(index));
    setDialogOpen(true)
  }
  const closeDialog = () => setDialogOpen(false)
  const confirmDialog = () => {
    if (dialogIndex === null) return
    setPerSentenceStatus(prev => {
      const next = [...prev]
      next[dialogIndex] = dialogStatus
      return next
    })
    onManualGradingChange?.(dialogIndex, dialogStatus);
    closeDialog()
  }

  const handleAiGrade = async (idx: number) => {
    if (isSentenceLocked(idx)) return;
    if (!canUseAi) {
      alert('AI 채점은 플러스 요금제 전용입니다.');
      return;
    }
    if (aiUsedSet.has(idx)) {
      alert('AI 채점은 문항당 1회만 실행할 수 있습니다.');
      return;
    }
    try {
      const recordingUrl = recordings[idx]
      // 녹음이 없으면 백엔드에서 오답 처리하므로 그대로 요청
      const scheduleId = (window as any).__CURRENT_SCHEDULE_ID__
      setAiLoadingSet(prev => { const s = new Set(prev); s.add(idx); return s })
      const res = await academyLearningScheduleApi.aiGradeDictation(scheduleId, {
        recordingUrl: recordingUrl || '',
        correctWord: sentenceItems[idx]?.text || ''
      })
      const correct = !!(res?.data?.correct)
      setPerSentenceStatus(s => { const n=[...s]; n[idx] = correct ? 'correct' : 'incorrect'; return n })
      onManualGradingChange?.(idx, correct ? 'correct' : 'incorrect')
      setAiUsedSet(prev => {
        const next = new Set(prev);
        next.add(idx);
        return next;
      });
    } catch (e) {
      alert('AI 채점에 실패했습니다.')
    } finally {
      setAiLoadingSet(prev => { const s = new Set(prev); s.delete(idx); return s })
    }
  }

  // 테두리 색상 결정 함수
  const getBorderColor = (status: 'correct' | 'incorrect' | 'grading') => {
    switch (status) {
      case 'correct':
        return 'border-green-500';
      case 'incorrect':
        return 'border-red-500';
      case 'grading':
        return 'border-gray-200';
      default:
        return 'border-gray-200';
    }
  };

  const renderRoleBadge = () => {
    const base = 'flex justify-center items-center text-xs font-bold rounded-full px-2 py-0.5';
    return <span className={`${base} bg-indigo-100 text-indigo-700`}>Q</span>;
  };

  return (
    <div>
        <div className='flex flex-col gap-2'>
            <p className='text-lg font-bold'>스토리앤 다이얼로그</p>
            {/* 상단: 전체 스크립트 */}
            <div className='flex flex-col gap-2 justify-center items-center p-5 rounded-2xl border border-gray-200'>
              <p className='text-lg font-bold pr-8'>
                {sentenceItems.length > 0 ? sentenceItems.map(item => item.text).join(' ') : '스크립트가 없습니다.'}
              </p>
            </div>
            {/* 문장별 채점 */}
            {sentenceItems.map((sentence, idx) => {
              const hasRecording = Boolean(recordings[idx]);
              const sentenceStatus = perSentenceStatus[idx];
              const sentenceLocked = isSentenceLocked(idx);
              const canGradeRecording = type === 'gradingCard' && hasRecording && !sentenceLocked;
              const showRecordingViewButton = hasRecording && (type !== 'gradingCard' || sentenceLocked);
              return (
                <div
                  key={sentence.id || `sd-${idx}`}
                  className={`flex flex-col rounded-2xl border overflow-hidden ${getBorderColor(sentenceStatus)}`}
                >
                  <div className='flex flex-col gap-3 px-4 py-3 border-b border-gray-100'>
                    <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
                      <div className='flex items-start gap-2'>
                        <span className='flex justify-center items-center text-xs font-semibold text-gray-500 mt-0.5'>{idx + 1}.</span>
                        {renderRoleBadge()}
                        <p className='font-bold text-gray-900'>{sentence.text || '스크립트가 없습니다.'}</p>
                      </div>
                      <div className='flex flex-wrap items-center justify-end gap-2'>
                        {sentence.audioUrl && (
                          <ListenButton audioSrc={sentence.audioUrl} size={40} className='!p-1' />
                        )}
                        <GradingState status={sentenceStatus} />
                      </div>
                    </div>
                  </div>
                  <div className='flex flex-col gap-3 px-4 py-4 bg-gray-50'>
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                      <div className='flex flex-wrap gap-2'>
                        {hasRecording ? (
                          <div className='flex flex-wrap gap-2'>
                            {canGradeRecording && (
                              <Button size='small' intent='secondary' onClick={() => openDialog(idx)}>
                                녹음 듣고 채점하기
                              </Button>
                            )}
                            {showRecordingViewButton && (
                              <Button size='small' intent='secondary' onClick={() => openDialog(idx)}>
                                녹음 듣기
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className='text-sm text-gray-500'>제출된 녹음이 없습니다.</span>
                        )}
                      </div>
                      <div className='flex flex-wrap gap-2'>
                        {canGradeRecording && canUseAi && (
                          <Button
                            size='small'
                            onClick={() => handleAiGrade(idx)}
                            disabled={aiLoadingSet.has(idx) || aiUsedSet.has(idx)}
                          >
                            {aiLoadingSet.has(idx) ? '채점중...' : aiUsedSet.has(idx) ? 'AI 채점 완료' : 'AI 채점'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {/* 녹음 듣기 다이얼로그 */}
        <Dialog open={dialogOpen} onCancel={closeDialog} onOk={(type === 'gradingCard' && !dialogLocked) ? confirmDialog : closeDialog} okText={(type === 'gradingCard' && !dialogLocked) ? '확인' : '닫기'} cancelText={(type === 'gradingCard' && !dialogLocked) ? '취소' : undefined}>
          <RecordingGradingContents
            word={dialogIndex !== null ? sentenceItems[dialogIndex]?.text || '' : ''}
            recordingUrl={dialogIndex !== null ? (recordings[dialogIndex] || undefined) : undefined}
            initialGradingStatus={dialogStatus}
            onGradingChange={(type === 'gradingCard' && !dialogLocked) ? setDialogStatus : undefined}
            readOnly={type !== 'gradingCard' || dialogLocked}
          />
        </Dialog>
    </div>
  )
}

export default GradingStoryDialog